import React, { useState, useEffect, useRef } from 'react'
import remoteLog from '../../../shared/remoteLog'

// Debug logging for iOS Safari production issue
const DEBUG = true
const log = (...args) => {
  if (DEBUG) {
    console.log('[WisdomDrag]', new Date().toISOString().slice(11, 23), ...args)
    // Also send to remote log for cucumber debugging
    remoteLog.debug(`[WisdomDrag] ${args[0]}`, args.slice(1))
  }
}

// Offset the dragged word above the touch point so it's visible while dragging
const DRAG_OFFSET_Y = 60

export default function WisdomGame({ wisdom, totalScore, targetScore, alreadyPenalized = false, onComplete, onEvent, onPenaltyEvent, railsEnv }) {
  const touchMoveCount = useRef(0)
  const [currentHintsUsed, setCurrentHintsUsed] = useState(0)
  const [wordBank, setWordBank] = useState([])
  const [answerArea, setAnswerArea] = useState([])
  const [resultMessage, setResultMessage] = useState(null)
  const [isCorrect, setIsCorrect] = useState(false)
  const [draggedItem, setDraggedItem] = useState(null)
  const [correctResult, setCorrectResult] = useState(null)
  // dragOverPosition tracks where to show the cursor: { wordIndex, side: 'left' | 'right' }
  // The actual insertion index is: side === 'left' ? wordIndex : wordIndex + 1
  const [dragOverPosition, setDragOverPosition] = useState(null)
  const [touchStartPos, setTouchStartPos] = useState(null)
  const [touchCurrentPos, setTouchCurrentPos] = useState(null)
  // Explanation quiz state
  const [showExplanationQuiz, setShowExplanationQuiz] = useState(false)
  const [explanationOptions, setExplanationOptions] = useState([])
  const [selectedExplanation, setSelectedExplanation] = useState(null)
  const [explanationQuizResult, setExplanationQuizResult] = useState(null) // null, 'correct', 'incorrect'

  const isTestMode = railsEnv === 'test' || railsEnv === 'development'

  const [isRevealingHint, setIsRevealingHint] = useState(false)

  const emitEvent = (event) => {
    if (onEvent) onEvent(event)
  }

  // Penalty events must reach the server before the UI reveals information
  // that depends on them (currently just: use_hint before hint words appear).
  // This defeats the two-tab race where Tab A gets the hint reveal and Tab B
  // races an answer_correctly through before the penalty syncs.
  const emitPenaltyEvent = async (event) => {
    if (onPenaltyEvent) return onPenaltyEvent(event)
    if (onEvent) onEvent(event)
  }

  // Initialize word bank when wisdom changes. Also emits "asked" so the store
  // can start a fresh attempt for anti-cheat bookkeeping.
  useEffect(() => {
    if (!wisdom) return
    log('Initializing wisdom', { id: wisdom.id, wordsCount: wisdom.words.length })

    emitEvent({ wisdom_id: wisdom.id, event: 'asked' })

    const shuffled = [...wisdom.words]
    shuffleArray(shuffled)
    setWordBank(shuffled.map((word, index) => ({ word, id: `${word}-${index}` })))
    setAnswerArea([])
    setCurrentHintsUsed(0)
    setResultMessage(null)
    setIsCorrect(false)
    setCorrectResult(null)
    setShowExplanationQuiz(false)
    setExplanationOptions([])
    setSelectedExplanation(null)
    setExplanationQuizResult(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wisdom])

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]]
    }
  }

  const buildExplanationOptions = () => {
    const options = [wisdom.meaning]
    if (wisdom.incorrect_explanation1) options.push(wisdom.incorrect_explanation1)
    if (wisdom.incorrect_explanation2) options.push(wisdom.incorrect_explanation2)
    const shuffled = [...options]
    shuffleArray(shuffled)
    return shuffled
  }

  const handleWordClick = (item, fromArea) => {
    if (isCorrect) return

    if (fromArea === 'bank') {
      setWordBank(prev => prev.filter(w => w.id !== item.id))
      setAnswerArea(prev => [...prev, item])
    } else {
      setAnswerArea(prev => prev.filter(w => w.id !== item.id))
      setWordBank(prev => [...prev, item])
    }
  }

  const handleDragStart = (e, item, fromArea) => {
    if (isCorrect) {
      e.preventDefault()
      return
    }
    setDraggedItem({ item, fromArea })
    e.dataTransfer.effectAllowed = 'move'
  }

  const getInsertionIndex = (position) => {
    if (!position) return null
    return position.side === 'left' ? position.wordIndex : position.wordIndex + 1
  }

  const handleDragOverWord = (e, wordIndex) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'

    if (draggedItem && draggedItem.item.id === answerArea[wordIndex]?.id) return

    const rect = e.currentTarget.getBoundingClientRect()
    const mouseX = e.clientX

    let side
    if (rect.width > 0) {
      const midpoint = rect.left + rect.width / 2
      side = mouseX < midpoint ? 'left' : 'right'
    } else {
      side = e.currentTarget.dataset.dropSide || 'left'
    }

    setDragOverPosition({ wordIndex, side })
  }

  const handleDragOverAnswerArea = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (answerArea.length > 0) {
      setDragOverPosition({ wordIndex: answerArea.length - 1, side: 'right' })
    } else {
      setDragOverPosition({ wordIndex: 0, side: 'left' })
    }
  }

  const handleDragLeave = (e) => {
    const answerAreaEl = e.currentTarget.closest('[data-testid="answer-area"]')
    if (!answerAreaEl || !answerAreaEl.contains(e.relatedTarget)) {
      setDragOverPosition(null)
    }
  }

  const handleDrop = (e, toArea, insertIndex = null) => {
    e.preventDefault()
    setDragOverPosition(null)

    if (!draggedItem || isCorrect) return

    const { item, fromArea } = draggedItem
    const effectiveInsertIndex = insertIndex ?? getInsertionIndex(dragOverPosition)

    if (fromArea === toArea && toArea === 'answer' && effectiveInsertIndex !== null) {
      setAnswerArea(prev => {
        const currentIndex = prev.findIndex(w => w.id === item.id)
        if (currentIndex === -1) return prev
        if (currentIndex === effectiveInsertIndex || currentIndex === effectiveInsertIndex - 1) return prev

        const newArray = prev.filter(w => w.id !== item.id)
        const adjustedIndex = currentIndex < effectiveInsertIndex ? effectiveInsertIndex - 1 : effectiveInsertIndex
        newArray.splice(adjustedIndex, 0, item)
        return newArray
      })
      setDraggedItem(null)
      return
    }

    if (fromArea === toArea && effectiveInsertIndex === null) {
      setDraggedItem(null)
      return
    }

    if (fromArea === 'bank') {
      setWordBank(prev => prev.filter(w => w.id !== item.id))
    } else {
      setAnswerArea(prev => prev.filter(w => w.id !== item.id))
    }

    if (toArea === 'bank') {
      setWordBank(prev => [...prev, item])
    } else if (effectiveInsertIndex !== null) {
      setAnswerArea(prev => {
        const filtered = prev.filter(w => w.id !== item.id)
        filtered.splice(effectiveInsertIndex, 0, item)
        return filtered
      })
    } else {
      setAnswerArea(prev => [...prev, item])
    }

    setDraggedItem(null)
  }

  // Touch event handlers for mobile
  const handleTouchStart = (e, item, fromArea) => {
    if (isCorrect || item.isHint) return
    const touch = e.touches[0]
    touchMoveCount.current = 0
    setTouchStartPos({ x: touch.clientX, y: touch.clientY })
    setDraggedItem({ item, fromArea })
  }

  const handleTouchMove = (e) => {
    touchMoveCount.current++
    if (!draggedItem || !touchStartPos) return

    e.preventDefault()

    const touch = e.touches[0]
    setTouchCurrentPos({ x: touch.clientX, y: touch.clientY })

    const detectY = touch.clientY - DRAG_OFFSET_Y
    const element = document.elementFromPoint(touch.clientX, detectY)
    if (!element) return

    const wordContainer = element.closest('[data-word-container]')
    const answerAreaEl = element.closest('[data-drop-zone="answer"]')

    if (wordContainer && answerAreaEl) {
      const wordIndex = parseInt(wordContainer.dataset.wordIndex)
      if (draggedItem.item.id === answerArea[wordIndex]?.id) return

      const rect = wordContainer.getBoundingClientRect()
      const midpoint = rect.left + rect.width / 2
      const side = touch.clientX < midpoint ? 'left' : 'right'
      setDragOverPosition({ wordIndex, side })
    } else if (answerAreaEl && !wordContainer) {
      const wordCount = answerAreaEl.querySelectorAll('[data-word-container]').length
      if (wordCount > 0) {
        setDragOverPosition({ wordIndex: wordCount - 1, side: 'right' })
      } else {
        setDragOverPosition({ wordIndex: 0, side: 'left' })
      }
    }
  }

  const handleTouchEnd = (e) => {
    if (!draggedItem || !touchStartPos) return

    const touch = e.changedTouches[0]
    const detectY = touch.clientY - DRAG_OFFSET_Y
    const element = document.elementFromPoint(touch.clientX, detectY)

    if (!element) {
      setDraggedItem(null)
      setTouchStartPos(null)
      setDragOverPosition(null)
      return
    }

    const wordContainer = element.closest('[data-word-container]')
    const answerAreaEl = element.closest('[data-drop-zone="answer"]')
    const wordBankEl = element.closest('[data-drop-zone="bank"]')

    if (wordContainer && answerAreaEl) {
      const insertIndex = getInsertionIndex(dragOverPosition)
      handleDrop({ preventDefault: () => {} }, 'answer', insertIndex)
    } else if (answerAreaEl && !wordContainer) {
      const wordCount = answerAreaEl.querySelectorAll('[data-word-container]').length
      handleDrop({ preventDefault: () => {} }, 'answer', wordCount)
    } else if (wordBankEl) {
      handleDrop({ preventDefault: () => {} }, 'bank')
    }

    setTouchStartPos(null)
    setTouchCurrentPos(null)
    setDragOverPosition(null)
  }

  const handleGiveHint = async () => {
    if (currentHintsUsed >= 4 || isCorrect || isRevealingHint) return

    // Only the first hint counts for scoring; wait for the server to record
    // the penalty before revealing anything (anti-cheat: see emitPenaltyEvent).
    if (currentHintsUsed === 0) {
      setIsRevealingHint(true)
      try {
        await emitPenaltyEvent({ wisdom_id: wisdom.id, event: 'use_hint' })
      } finally {
        setIsRevealingHint(false)
      }
    }

    const hintNumber = currentHintsUsed + 1
    const targetWordsToReveal = Math.ceil(wisdom.words.length * hintNumber / 4)

    const currentWordBank = wordBank
    const currentAnswerArea = answerArea

    let correctlyPlacedAndHinted = 0
    for (let i = 0; i < targetWordsToReveal; i++) {
      const itemAtPosition = currentAnswerArea[i]
      if (itemAtPosition && itemAtPosition.word === wisdom.words[i] && itemAtPosition.isHint) {
        correctlyPlacedAndHinted++
      }
    }

    if (correctlyPlacedAndHinted >= targetWordsToReveal) {
      setCurrentHintsUsed(prev => prev + 1)
      return
    }

    const usedIds = new Set()
    const newAnswerArray = []

    for (let i = 0; i < targetWordsToReveal; i++) {
      const correctWord = wisdom.words[i]
      const wordInAnswer = currentAnswerArea.find(item => item.word === correctWord && !usedIds.has(item.id))
      const wordInBank = currentWordBank.find(item => item.word === correctWord && !usedIds.has(item.id))
      const wordItem = wordInAnswer || wordInBank

      if (wordItem) {
        newAnswerArray.push({ ...wordItem, isHint: true })
        usedIds.add(wordItem.id)
      }
    }

    currentAnswerArea.forEach(item => {
      if (!usedIds.has(item.id)) newAnswerArray.push(item)
    })

    const newWordBank = currentWordBank.filter(item => !usedIds.has(item.id))

    setAnswerArea(newAnswerArray)
    setWordBank(newWordBank)
    setCurrentHintsUsed(prev => prev + 1)
  }

  const handleCheckAnswer = () => {
    if (isCorrect) return

    const userAnswer = answerArea.map(item => item.word)
    const correct = JSON.stringify(userAnswer) === JSON.stringify(wisdom.words)

    if (correct) {
      // Score decided locally (mirrors server rule): 1 iff no hint used in this
      // attempt. `alreadyPenalized` covers the case where the kid used a hint
      // in a previous session for this wisdom and reloaded — local state resets
      // but the persistent flag remembers.
      const score = (currentHintsUsed === 0 && !alreadyPenalized) ? 1 : 0
      const mastered = score === 1

      emitEvent({ wisdom_id: wisdom.id, event: 'answer_correctly' })

      setIsCorrect(true)
      setCorrectResult({
        wisdomId: wisdom.id,
        score,
        hintsUsed: currentHintsUsed,
        mastered
      })

      setExplanationOptions(buildExplanationOptions())
      setShowExplanationQuiz(true)

      setResultMessage({
        type: 'success',
        text: wisdom.has_explanation_quiz
          ? 'Chính xác! Bây giờ hãy chọn ý nghĩa đúng của câu tục ngữ:'
          : 'Chính xác! Hãy xem ý nghĩa của câu tục ngữ:'
      })
    } else {
      setResultMessage({ type: 'danger', text: 'Chưa đúng, hãy thử lại!' })
    }
  }

  const handleExplanationSelect = (explanation) => {
    if (explanationQuizResult) return

    setSelectedExplanation(explanation)
    const isCorrectExplanation = explanation === wisdom.meaning

    if (isCorrectExplanation) {
      setExplanationQuizResult('correct')
      const baseScore = correctResult.score
      let message = baseScore >= 1
        ? '<strong>Chính xác! +1 điểm (Đã thành thạo!)</strong>'
        : '<strong>Chính xác! +0 điểm (Cần luyện thêm)</strong>'
      message += `<div class="mt-2"><strong>Ý nghĩa:</strong> ${wisdom.meaning}</div>`
      setResultMessage({ type: 'success', html: message })
    } else {
      setExplanationQuizResult('incorrect')
      emitEvent({ wisdom_id: wisdom.id, event: 'wrong_explanation' })
      setCorrectResult(prev => ({ ...prev, score: 0, mastered: false }))
      let message = '<strong>Chưa đúng! +0 điểm (Cần luyện thêm)</strong>'
      message += `<div class="mt-2 text-warning"><strong>Ý nghĩa đúng:</strong> ${wisdom.meaning}</div>`
      setResultMessage({ type: 'warning', html: message })
    }
  }

  const handleNextWisdom = () => {
    if (correctResult) onComplete(correctResult)
  }

  return (
    <div className="wisdom-game">
      {/* Progress indicator */}
      <div className="progress-section" {...(isTestMode && { 'data-testid': 'progress-indicator' })}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-semibold text-secondary">Tiến độ:</span>
          <span className="fw-bold" style={{ color: '#6d28d9' }} {...(isTestMode && { 'data-testid': 'score-display' })}>
            {totalScore}/{targetScore} điểm
          </span>
        </div>
        <div className="progress" style={{ height: '10px', borderRadius: '9999px', backgroundColor: '#e5e7eb' }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{
              width: `${targetScore > 0 ? (totalScore / targetScore) * 100 : 0}%`,
              backgroundColor: '#6d28d9',
              borderRadius: '9999px'
            }}
            aria-valuenow={totalScore}
            aria-valuemin="0"
            aria-valuemax={targetScore}
          />
        </div>
      </div>

      {/* Answer area */}
      {!showExplanationQuiz && (
      <div className="answer-section">
        <h5 className="mb-3">Câu trả lời của bạn:</h5>
        <div
          className="drop-zone answer-area"
          data-drop-zone="answer"
          onDragOver={handleDragOverAnswerArea}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'answer')}
          {...(isTestMode && { 'data-testid': 'answer-area' })}
        >
          {answerArea.length === 0 && (
            <div
              className="empty-placeholder"
              style={{ position: 'relative' }}
              onDragOver={handleDragOverAnswerArea}
              onDrop={(e) => handleDrop(e, 'answer', 0)}
            >
              {draggedItem && dragOverPosition ? (
                <div className="insertion-cursor" style={{ left: '50%' }} />
              ) : (
                'Kéo các từ vào đây'
              )}
            </div>
          )}
          {answerArea.map((item, index) => (
            <div
              key={item.id}
              data-word-container
              data-word-index={index}
              style={{
                position: 'relative',
                display: 'flex',
                padding: '0 0.25rem',
                flex: '0 0 auto'
              }}
              onDragOver={(e) => handleDragOverWord(e, index)}
              onDrop={(e) => {
                e.stopPropagation()
                handleDrop(e, 'answer')
              }}
            >
              {draggedItem && dragOverPosition?.wordIndex === index && dragOverPosition?.side === 'left' && draggedItem.item.id !== item.id && (
                <div className="insertion-cursor" style={{ left: 0 }} />
              )}
              <button
                type="button"
                className={`word-button ${item.isHint ? 'hint-word' : ''}`}
                draggable={!isCorrect && !item.isHint}
                onDragStart={(e) => handleDragStart(e, item, 'answer')}
                onTouchStart={(e) => handleTouchStart(e, item, 'answer')}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={() => !item.isHint && handleWordClick(item, 'answer')}
                disabled={item.isHint || isCorrect}
                {...(isTestMode && { 'data-testid': `answer-word-${index}` })}
              >
                {item.word}
              </button>
              {draggedItem && dragOverPosition?.wordIndex === index && dragOverPosition?.side === 'right' && draggedItem.item.id !== item.id && (
                <div className="insertion-cursor" style={{ right: 0 }} />
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Word bank */}
      {!showExplanationQuiz && (
      <div className="word-bank-section">
        <h5 className="mb-3">Các từ:</h5>
        <div
          className="drop-zone word-bank"
          data-drop-zone="bank"
          onDragOver={(e) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setDragOverPosition(null)
          }}
          onDrop={(e) => handleDrop(e, 'bank')}
          {...(isTestMode && { 'data-testid': 'word-bank' })}
        >
          {wordBank.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="word-button"
              draggable={!isCorrect}
              onDragStart={(e) => handleDragStart(e, item, 'bank')}
              onTouchStart={(e) => handleTouchStart(e, item, 'bank')}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => handleWordClick(item, 'bank')}
              disabled={isCorrect}
              {...(isTestMode && { 'data-testid': `bank-word-${index}` })}
            >
              {item.word}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Action buttons */}
      {!showExplanationQuiz && (
      <div className="action-buttons-new">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleGiveHint}
            disabled={currentHintsUsed >= 4 || isCorrect || isRevealingHint}
            style={{ borderRadius: '8px' }}
            {...(isTestMode && { 'data-testid': 'hint-button' })}
          >
            {isRevealingHint ? 'Đang mở gợi ý…' : (
              <>💡 Gợi ý {currentHintsUsed > 0 && `(${currentHintsUsed}/4)`}</>
            )}
          </button>

          {isTestMode && (
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => {
                emitEvent({ wisdom_id: wisdom.id, event: 'answer_correctly' })
                onComplete({
                  wisdomId: wisdom.id,
                  score: 1,
                  hintsUsed: 0,
                  mastered: true
                })
              }}
              disabled={isCorrect}
              data-testid="fake-completion-button"
            >
              Fake 1 Completion
            </button>
          )}
        </div>

        <div className="d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-lg fw-bold"
            onClick={handleCheckAnswer}
            disabled={isCorrect || answerArea.length === 0}
            style={{
              backgroundColor: '#6d28d9',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 48px',
              fontSize: '18px',
              boxShadow: '0 4px 6px #e9d5ff, 0 10px 15px #e9d5ff',
              minWidth: '200px',
              opacity: (isCorrect || answerArea.length === 0) ? 0.5 : 1
            }}
            {...(isTestMode && { 'data-testid': 'check-button' })}
          >
            Kiểm tra
          </button>
        </div>
      </div>
      )}

      {/* Result message */}
      {resultMessage && (
        <div
          className={`result-banner d-flex align-items-start gap-3 ${resultMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}
          style={{
            backgroundColor: resultMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
            borderRadius: '8px',
            padding: '16px 20px',
            marginTop: '16px'
          }}
          role="alert"
          {...(isTestMode && { 'data-testid': 'result-message' })}
        >
          <div style={{ flexShrink: 0, marginTop: '2px' }}>
            {resultMessage.type === 'success' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#065f46" viewBox="0 0 512 512">
                <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#991b1b" viewBox="0 0 512 512">
                <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/>
              </svg>
            )}
          </div>
          <div style={{ color: resultMessage.type === 'success' ? '#065f46' : '#991b1b', fontSize: '16px' }}>
            {resultMessage.html ? (
              <span dangerouslySetInnerHTML={{ __html: resultMessage.html }} />
            ) : (
              resultMessage.text
            )}
          </div>
        </div>
      )}

      {/* Explanation quiz */}
      {showExplanationQuiz && (
        <div className="explanation-quiz mt-4" {...(isTestMode && { 'data-testid': 'explanation-quiz' })}>
          <h5 className="fw-semibold mb-4" style={{ fontSize: '20px', color: '#1f2937' }}>Chọn ý nghĩa đúng:</h5>
          <div className="d-flex flex-column gap-3">
            {explanationOptions.map((option, index) => {
              let cardStyle = {
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                cursor: explanationQuizResult ? 'default' : 'pointer',
                transition: 'all 0.2s ease'
              }

              let cardClasses = 'd-flex align-items-start gap-3'

              if (explanationQuizResult) {
                if (option === wisdom.meaning) {
                  cardStyle.backgroundColor = '#d1fae5'
                  cardStyle.border = '2px solid #10b981'
                  cardClasses += ' border-success'
                } else if (selectedExplanation === option) {
                  cardStyle.backgroundColor = '#fee2e2'
                  cardStyle.border = '2px solid #ef4444'
                  cardClasses += ' border-danger'
                }
              } else if (selectedExplanation === option) {
                cardStyle.backgroundColor = 'white'
                cardStyle.border = '2px solid #6d28d9'
              }

              return (
                <label
                  key={index}
                  className={cardClasses}
                  style={cardStyle}
                  {...(isTestMode && { 'data-testid': `explanation-option-${index}` })}
                >
                  <input
                    type="radio"
                    name="explanation"
                    className="form-check-input mt-1"
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#6d28d9',
                      flexShrink: 0
                    }}
                    checked={selectedExplanation === option}
                    onChange={() => !explanationQuizResult && setSelectedExplanation(option)}
                    disabled={explanationQuizResult !== null}
                  />
                  <span style={{ color: '#374151', fontSize: '16px', lineHeight: '24px' }}>
                    {option}
                  </span>
                </label>
              )
            })}
          </div>
          {!explanationQuizResult && (
            <div className="mt-4 d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-lg fw-bold"
                onClick={() => handleExplanationSelect(selectedExplanation)}
                disabled={!selectedExplanation}
                style={{
                  backgroundColor: '#6d28d9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 48px',
                  fontSize: '18px',
                  boxShadow: '0 4px 6px #e9d5ff, 0 10px 15px #e9d5ff',
                  minWidth: '200px',
                  opacity: selectedExplanation ? 1 : 0.5
                }}
                {...(isTestMode && { 'data-testid': 'confirm-explanation-button' })}
              >
                Xác nhận
              </button>
            </div>
          )}
        </div>
      )}

      {isCorrect && (!showExplanationQuiz || explanationQuizResult) && (
        <div className="next-button-container">
          <button
            type="button"
            className="btn btn-success btn-lg"
            onClick={handleNextWisdom}
            {...(isTestMode && { 'data-testid': 'next-button' })}
          >
            Câu tiếp theo →
          </button>
        </div>
      )}

      {/* Floating dragged word for touch devices */}
      {draggedItem && touchCurrentPos && (
        <div
          className="dragged-word-floating"
          style={{
            position: 'fixed',
            left: touchCurrentPos.x,
            top: touchCurrentPos.y - DRAG_OFFSET_Y,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            zIndex: 9999,
            opacity: 0.8
          }}
        >
          <div className="word-button">
            {draggedItem.item.word}
          </div>
        </div>
      )}
    </div>
  )
}
